import {defineStore} from "pinia";
import {setCssVar} from "quasar";
import ColorUtils from "@/utils/common/ColorUtils.ts";
import FontSize from "@/utils/constants/FontSize.ts";
import YesNoDefault from "@/utils/constants/YesNoDefault.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import {useNamaBreakpoints} from "@/composables/useNamaBreakpoints.ts";
import {unref} from "vue";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import DTOVueGUITheme = namaTypes.DTOVueGUITheme;
import DTOGUIColorTheme = namaTypes.DTOGUIColorTheme;


const {isDesktop} = useNamaBreakpoints();
const namaDefaultTheme: DTOVueGUITheme = {
  code: "namaDefaultTheme",
  id: "nama-default-theme",
  name1: "الثيم الإفتراضي",
  name2: "Default Theme",
  lightColorTheme: {
    primaryColor: "#0C82AA",
    primaryBackgroundColor: "#F8F9FA",
    fontColor: "#000000",
    iconsColor: "#48bfe6",
    warningColor: "#DC3545"
  },
  darkColorTheme: {
    primaryColor: "#18ADE3",
    primaryBackgroundColor: "#383838",
    fontColor: "#FFFFFF",
    iconsColor: "#2097be",
    warningColor: "#CF6679"
  },
  fontSize: "Medium",
  darkMode: "DefaultValue",
  defaultTheme: false
};
function isDark(darkMode: string | undefined): boolean {
  if (!darkMode || darkMode === YesNoDefault.DefaultValue)
    return localStorage.getItem('color-theme') === 'dark' || (!('color-theme' in localStorage)
      && window.matchMedia('(prefers-color-scheme: dark)').matches) || false;
  if (darkMode === YesNoDefault.No)
    return false;
  return darkMode === YesNoDefault.Yes;
}
export const useThemeStore = defineStore('themeStore', {
  state: () => ({
    theme: {} as DTOVueGUITheme,
  }), actions: {
    async fetchAndApplyTheme(newCode?: string) {
      let code = newCode || localStorage.getItem('theme-code');
      let theme: DTOVueGUITheme = namaDefaultTheme;
      if (code !== "namaDefaultTheme") {
        let response = await NamaCommon.namaFetch(NamaURLS.FetchGUIThemes, {params: {code}});
        if (response) {
          theme = response
        }
      }
      this.applyTheme(theme);
      if (newCode)
        localStorage.setItem('theme-code', newCode);
    }, async fetchListThemes() {
      let response: DTOVueGUITheme[] | any = await NamaCommon.namaFetch(NamaURLS.FetchGUIThemes, {params: {list: true}});
      if (!response || !response.length)
        response = [];
      response.unshift(namaDefaultTheme);
      return response;
    }, applyTheme(theme: DTOVueGUITheme) {
      this.updatePrimaryColor(theme.lightColorTheme?.primaryColor);
      this.updatePrimaryDarkColor(theme.darkColorTheme?.primaryColor);
      this.updateBackgroundLightColor(theme.lightColorTheme?.primaryBackgroundColor);
      this.updateSecondaryBackgroundLightColor(theme.lightColorTheme?.secondaryBackgroundColor);
      this.updateThirdBackgroundLightColor(theme.lightColorTheme?.thirdBackgroundColor);
      this.updateBackgroundDarkColor(theme.darkColorTheme?.primaryBackgroundColor);
      this.updateSecondaryBackgroundDarkColor(theme.darkColorTheme?.secondaryBackgroundColor);
      this.updateThirdBackgroundDarkColor(theme.darkColorTheme?.thirdBackgroundColor);
      this.updateWarningLightColor(theme.lightColorTheme?.warningColor);
      this.updateWarningDarkColor(theme.darkColorTheme?.warningColor);
      this.updateFontLightColor(theme.lightColorTheme?.fontColor);
      this.updateFontDarkColor(theme.darkColorTheme?.fontColor);
      this.updateFontSize(theme.fontSize);
      this.updateDarkMode(theme.darkMode);
      this.updateDensityMode(theme.densityMode, theme.compactMode);
      this.updateToolbarLabel(theme.showToolbarLabels);
      this.updateQuadLayout(theme.quadLayout);
      this.updateHorizontalMode(theme.horizontalMode);
    }, updatePrimaryColor(value: string | undefined) {
      if (!this.theme.lightColorTheme)
        this.theme.lightColorTheme = {} as DTOGUIColorTheme;
      this.theme.lightColorTheme.primaryColor = value;
      document.documentElement.style.setProperty('--nama-primary-light', this.getPrimaryColor());
      if (!isDark(this.theme.darkMode))
        setCssVar('primary', this.getPrimaryColor());
    }, updatePrimaryDarkColor(value: string | undefined) {
      if (!this.theme.darkColorTheme)
        this.theme.darkColorTheme = {} as DTOGUIColorTheme;
      this.theme.darkColorTheme.primaryColor = value;
      document.documentElement.style.setProperty('--nama-primary-dark', this.getPrimaryDarkColor());
      if (isDark(this.theme.darkMode))
        setCssVar('primary', this.getPrimaryDarkColor());
    }, updateBackgroundLightColor(value: string | undefined, secondary?: string, third?: string) {
      if (!this.theme.lightColorTheme)
        this.theme.lightColorTheme = {} as DTOGUIColorTheme;
      this.theme.lightColorTheme.primaryBackgroundColor = value;
      document.documentElement.style.setProperty('--nama-background-bright-light', this.getBackgroundBrightLightColor());
      const primaryBackgroundColor = this.getBackgroundBrightLightColor(true);
      if (!secondary) {
        this.theme.lightColorTheme.secondaryBackgroundColor = ColorUtils.rgbStringToHex(ColorUtils.adjustRGB(primaryBackgroundColor, -7));
        document.documentElement.style.setProperty('--nama-background-deep-light', this.getBackgroundDeepLightColor());
      }
      if (!third) {
        this.theme.lightColorTheme.thirdBackgroundColor = ColorUtils.rgbStringToHex(ColorUtils.adjustRGB(primaryBackgroundColor, -15));
        document.documentElement.style.setProperty('--nama-background-dim-light', this.getBackgroundDimLightColor());
      }
    }, updateSecondaryBackgroundLightColor(value: string | undefined) {
      if (!this.theme.lightColorTheme)
        this.theme.lightColorTheme = {} as DTOGUIColorTheme;
      this.theme.lightColorTheme.secondaryBackgroundColor = value ?? ColorUtils.rgbStringToHex(ColorUtils.adjustRGB(this.getBackgroundBrightLightColor(true), -7));
      document.documentElement.style.setProperty('--nama-background-deep-light', this.getBackgroundDeepLightColor());
    }, updateThirdBackgroundLightColor(value: string | undefined) {
      if (!this.theme.lightColorTheme)
        this.theme.lightColorTheme = {} as DTOGUIColorTheme;
      this.theme.lightColorTheme.thirdBackgroundColor = value ?? ColorUtils.rgbStringToHex(ColorUtils.adjustRGB(this.getBackgroundBrightLightColor(true), -15));
      document.documentElement.style.setProperty('--nama-background-dim-light', this.getBackgroundDimLightColor());
    }, updateBackgroundDarkColor(value: string | undefined, secondary?: string, third?: string) {
      if (!this.theme.darkColorTheme)
        this.theme.darkColorTheme = {} as DTOGUIColorTheme;
      this.theme.darkColorTheme.primaryBackgroundColor = value;
      document.documentElement.style.setProperty('--nama-background-bright-dark', this.getBackgroundBrightDarkColor());
      const primaryBackgroundColor = this.getBackgroundBrightDarkColor(true);
      if (!secondary) {
        this.theme.darkColorTheme.secondaryBackgroundColor = ColorUtils.rgbStringToHex(ColorUtils.adjustRGB(primaryBackgroundColor, -10));
        document.documentElement.style.setProperty('--nama-background-deep-dark', this.getBackgroundDeepDarkColor());
      }
      if (!third) {
        this.theme.darkColorTheme.thirdBackgroundColor = ColorUtils.rgbStringToHex(ColorUtils.adjustRGB(primaryBackgroundColor, -20));
        document.documentElement.style.setProperty('--nama-background-dim-dark', this.getBackgroundDimDarkColor());
      }
    }, updateSecondaryBackgroundDarkColor(value: string | undefined) {
      if (!this.theme.darkColorTheme)
        this.theme.darkColorTheme = {} as DTOGUIColorTheme;
      this.theme.darkColorTheme.secondaryBackgroundColor = value ?? ColorUtils.rgbStringToHex(ColorUtils.adjustRGB(this.getBackgroundBrightDarkColor(true), -10));
      document.documentElement.style.setProperty('--nama-background-deep-dark', this.getBackgroundDeepDarkColor());
    }, updateThirdBackgroundDarkColor(value: string | undefined) {
      if (!this.theme.darkColorTheme)
        this.theme.darkColorTheme = {} as DTOGUIColorTheme;
      this.theme.darkColorTheme.thirdBackgroundColor = value ?? ColorUtils.rgbStringToHex(ColorUtils.adjustRGB(this.getBackgroundBrightDarkColor(true), -20));
      document.documentElement.style.setProperty('--nama-background-dim-dark', this.getBackgroundDimDarkColor());
    }, updateWarningLightColor(value: string | undefined) {
      if (!this.theme.lightColorTheme)
        this.theme.lightColorTheme = {} as DTOGUIColorTheme;
      this.theme.lightColorTheme.warningColor = value;
      document.documentElement.style.setProperty('--nama-warning-light', this.getWarningLightColor());
    }, updateWarningDarkColor(value: string | undefined) {
      if (!this.theme.darkColorTheme)
        this.theme.darkColorTheme = {} as DTOGUIColorTheme;
      this.theme.darkColorTheme.warningColor = value;
      document.documentElement.style.setProperty('--nama-warning-dark', this.getWarningDarkColor());
    }, updateFontLightColor(value: string | undefined) {
      if (!this.theme.lightColorTheme)
        this.theme.lightColorTheme = {} as DTOGUIColorTheme;
      this.theme.lightColorTheme.fontColor = value;
      document.documentElement.style.setProperty('--nama-font-light', this.getFontLightColor());
    }, updateFontDarkColor(value: string | undefined) {
      if (!this.theme.darkColorTheme)
        this.theme.darkColorTheme = {} as DTOGUIColorTheme;
      this.theme.darkColorTheme.fontColor = value;
      document.documentElement.style.setProperty('--nama-font-dark', this.getFontDarkColor());
    }, updateFontSize(value: string | undefined) {
      if (!value)
        value = value ?? FontSize.Medium;
      this.theme.fontSize = value;
      document.documentElement.style.setProperty('--nama-font-size', (2 * (fontSizeToNumeric(value) - 3)) + 'px');
    }, updateDarkMode(value: string | undefined) {
      let colorTheme = localStorage.getItem('color-theme');
      if (ObjectChecker.isNullOrUndefined(colorTheme)) {
        if (!value)
          value = value ?? YesNoDefault.DefaultValue;
        this.theme.darkMode = value;
        let booleanValue = false;
        if (value === YesNoDefault.Yes)
          booleanValue = true;
        else if (value === YesNoDefault.DefaultValue)
          booleanValue = localStorage.getItem('color-theme') === 'dark' || (!('color-theme' in localStorage)
            && window.matchMedia('(prefers-color-scheme: dark)').matches);
        useNamaFlagsStore().updateDarkMde(booleanValue);
      }
    }, updateDensityMode(densityValue: string | undefined, legacyCompactMode?: string | undefined) {
      if (densityValue) {
        this.theme.densityMode = densityValue;
        useNamaFlagsStore().updateDensityMode(densityValue);
        return;
      }
      if (legacyCompactMode) {
        let density = 'Normal';
        if (legacyCompactMode === YesNoDefault.Yes)
          density = 'Compact';
        else if (legacyCompactMode === YesNoDefault.DefaultValue)
          density = localStorage.getItem('density-mode') || (NamaCommon.getBooleanFromStorageOrDefault('compact-mode', unref(isDesktop)) ? 'Compact' : 'Normal');
        useNamaFlagsStore().updateDensityMode(density);
        return;
      }
      let stored = localStorage.getItem('density-mode');
      if (stored) {
        useNamaFlagsStore().updateDensityMode(stored);
      }
    }, updateToolbarLabel(value: string | undefined) {
      if (!value)
        value = value ?? YesNoDefault.DefaultValue;
      this.theme.showToolbarLabels = value;
      let booleanValue = false;
      if (value === YesNoDefault.Yes)
        booleanValue = true;
      else if (value === YesNoDefault.DefaultValue)
        booleanValue = NamaCommon.getBooleanFromStorageOrDefault("show-toolbar-labels", unref(isDesktop));
      useNamaFlagsStore().updateToolbarLabel(booleanValue);
    }, updateQuadLayout(value: string | undefined) {
      if (!value)
        value = value ?? YesNoDefault.DefaultValue;
      this.theme.quadLayout = value;
      let booleanValue = false;
      if (value === YesNoDefault.Yes)
        booleanValue = true;
      else if (value === YesNoDefault.DefaultValue)
        booleanValue = NamaCommon.getBooleanFromStorageOrDefault('quad-layout', false);
      useNamaFlagsStore().updateQuadLayout(booleanValue);
    }, updateHorizontalMode(value: string | undefined) {
      if (!value)
        value = value ?? YesNoDefault.DefaultValue;
      this.theme.horizontalMode = value;
      let booleanValue = false;
      if (value === YesNoDefault.Yes)
        booleanValue = true;
      else if (value === YesNoDefault.DefaultValue)
        booleanValue = NamaCommon.getBooleanFromStorageOrDefault("horizontal-mode", unref(isDesktop));
      useNamaFlagsStore().updateHorizontalMode(booleanValue);
    }, getPrimaryColor(isRgb?: boolean) {
      if (isRgb)
        return ColorUtils.hexToRgb(this.theme.lightColorTheme?.primaryColor) ?? '12,130,170';
      return this.theme.lightColorTheme?.primaryColor ?? '#0c82aa';
    }, getPrimaryDarkColor(isRgb?: boolean) {
      if (isRgb)
        return ColorUtils.hexToRgb(this.theme.darkColorTheme?.primaryColor) ?? '24,173,227';
      return this.theme.darkColorTheme?.primaryColor ?? '#18ade3';
    }, getBackgroundBrightLightColor(isRgb?: boolean) {
      if (isRgb)
        return ColorUtils.hexToRgb(this.theme.lightColorTheme?.primaryBackgroundColor) ?? '248,249,250';
      return this.theme.lightColorTheme?.primaryBackgroundColor ?? '#F8F9FA';
    }, getBackgroundBrightDarkColor(isRgb?: boolean) {
      if (isRgb)
        return ColorUtils.hexToRgb(this.theme.darkColorTheme?.primaryBackgroundColor) ?? '56,56,56';
      return this.theme.darkColorTheme?.primaryBackgroundColor ?? '#383838';
    }, getBackgroundDeepLightColor(isRgb?: boolean) {
      if (isRgb)
        return ColorUtils.hexToRgb(this.theme.lightColorTheme?.secondaryBackgroundColor) ?? '253,254,255';
      return this.theme.lightColorTheme?.secondaryBackgroundColor ?? '#fdfeff';
    }, getBackgroundDeepDarkColor(isRgb?: boolean) {
      if (isRgb)
        return ColorUtils.hexToRgb(this.theme.darkColorTheme?.secondaryBackgroundColor) ?? '46,46,46';
      return this.theme.darkColorTheme?.secondaryBackgroundColor ?? '#2e2e2e';
    }, getBackgroundDimLightColor(isRgb?: boolean) {
      if (isRgb)
        return ColorUtils.hexToRgb(this.theme.lightColorTheme?.thirdBackgroundColor) ?? '243,244,246';
      return this.theme.lightColorTheme?.thirdBackgroundColor ?? '#f3f4f6';
    }, getBackgroundDimDarkColor(isRgb?: boolean) {
      if (isRgb)
        return ColorUtils.hexToRgb(this.theme.darkColorTheme?.thirdBackgroundColor) ?? '37,37,37';
      return this.theme.darkColorTheme?.thirdBackgroundColor ?? '#252525';
    }, getWarningLightColor(isRgb?: boolean) {
      if (isRgb)
        return ColorUtils.hexToRgb(this.theme.lightColorTheme?.warningColor) ?? '220,53,69';
      return this.theme.lightColorTheme?.warningColor ?? '#dc3545';
    }, getWarningDarkColor(isRgb?: boolean) {
      if (isRgb)
        return ColorUtils.hexToRgb(this.theme.darkColorTheme?.warningColor) ?? '207,102,121';
      return this.theme.darkColorTheme?.warningColor ?? '#cf6679';
    }, getFontLightColor(isRgb?: boolean) {
      if (isRgb)
        return ColorUtils.hexToRgb(this.theme.lightColorTheme?.fontColor) ?? '0,0,0';
      return this.theme.lightColorTheme?.fontColor ?? '#000000';
    }, getFontDarkColor(isRgb?: boolean) {
      if (isRgb)
        return ColorUtils.hexToRgb(this.theme.darkColorTheme?.fontColor) ?? '255,255,255';
      return this.theme.darkColorTheme?.fontColor ?? '#ffffff';
    }, getFontSize(): string {
      let fontSize = fontSizeToNumeric(this.theme.fontSize ?? FontSize.Medium);
      return (2 * (+fontSize - 3)) + 'px';
    }
  }
});

export function fontSizeToNumeric(size: string | undefined): number {
  if (!size)
    return 3;
  switch (size) {
    case FontSize.VeryLarge:
      return 5;
    case FontSize.Large:
      return 4;
    case FontSize.Medium:
      return 3;
    case FontSize.Small:
      return 2;
    case FontSize.VerySmall:
      return 1;
    default:
      return 3;
  }
}

function numericToFontSize(number: number | string | undefined): string {
  if (!number)
    return FontSize.Medium;
  switch (number) {
    case 5:
      return FontSize.VeryLarge;
    case 4:
      return FontSize.Large;
    case 3:
      return FontSize.Medium;
    case 2:
      return FontSize.Small;
    case 1:
      return FontSize.VerySmall;
    default:
      return FontSize.Medium;
  }
}
